import React, { useContext } from 'react'
import { t } from '@lingui/macro'
import { ManageCampaignContext } from '../ManageCampaignPage'
import { DownloadCSVButton } from '../../../components/common/download-csv-button/DownloadCSVButton'
import { ICampaignActionReportFilters } from './CampaignActionReport'
import {
  hasNotificationError,
  IScheduledAction,
  ReportTypeEnum
} from './CampaignActionReportOverviewTable'

import { CampaignUserEventTypeEnum } from '../../../../../shared/enums/campaigns'

import {
  CampaignUserReportRow,
  ScheduledActionReport,
  EnrolmentEvent,
  CampaignPathwayShareActionModelAsEditor,
  CampaignCertificationShareActionModel,
  CampaignBannerModelAsEditor,
  CampaignEmailActionModelAsEditor,
  CampaignMessageActionModelAsEditor,
  CampaignSurveyShareActionModelAsEditor,
  CampaignTopicShareActionModelAsEditor
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { CampaignReportsDocument } from './CampaignActionReport.codegen'

interface ICampaignReportDownloadCSVProps {
  itemsCount: number
  filters: ICampaignActionReportFilters
  sortedTimeline: any
}

interface ICsvHeader {
  actionName: string
  scheduledAction: IScheduledAction
  actionType: ReportTypeEnum
}

const CampaignActionReportDownloadCSV = ({
  itemsCount,
  filters,
  sortedTimeline
}: ICampaignReportDownloadCSVProps) => {
  const { campaign } = useContext(ManageCampaignContext)
  const csvColumns = buildCsvHeaderColumns(sortedTimeline)
  return (
    <DownloadCSVButton
      customBatchSize={500}
      count={itemsCount}
      fileName={`looop-campaign-report-${campaign.id}`}
      dataPath={'data.admin.campaign.report.users'}
      queryOptions={(page, limit) => ({
        variables: {
          campaignId: campaign.id,
          filter: {
            displayName: filters.search.length > 0 ? filters.search : null,
            page: page - 1,
            limit,
            group_ids: filters.groupIds.length > 0 ? filters.groupIds : null
          }
        },
        query: CampaignReportsDocument,
        fetchPolicy: 'no-cache'
      })}
      prepareData={(data) => {
        return data.map((row: CampaignUserReportRow) => ({
          ...mapUserDataToCsvColumns(row, csvColumns)
        }))
      }}
    />
  )
}

const buildCsvHeaderColumns = (sortedTimeline): Array<ICsvHeader> => {
  let index = 1
  const tableColumns: Array<ICsvHeader> = []
  Object.keys(sortedTimeline).forEach((day) => {
    Object.keys(sortedTimeline[day])
      .sort()
      .forEach((hour) => {
        sortedTimeline[day][hour].forEach((scheduledAction) => {
          if (!scheduledAction.bannerHide) {
            tableColumns.push(
              ...createCsvHeaderColumn(scheduledAction, index++)
            )
          }
        })
      })
  })
  return tableColumns
}

const createCsvHeaderColumn = (
  scheduledAction: IScheduledAction,
  actionOrder: number
): Array<ICsvHeader> => {
  const headerObject: ICsvHeader = {
    actionName: '',
    scheduledAction: null as any,
    actionType: null as any
  }
  const headerObjectList: ICsvHeader[] = []
  headerObject.scheduledAction = scheduledAction

  if (
    scheduledAction.surveyShareAction ||
    scheduledAction.topicShareAction ||
    scheduledAction.pathwayShareAction ||
    scheduledAction.certificationShareAction ||
    scheduledAction.email
  ) {
    headerObject.actionName = t`${actionOrder}. Share `
    if (scheduledAction.topicShareAction) {
      const topicsCount = scheduledAction.topicShareAction.actionTopics.length
      headerObject.actionName +=
        topicsCount > 1
          ? t`${topicsCount} resources`
          : t`${scheduledAction.topicShareAction.actionTopics[0].topic.currentTitle} resource`
    }

    if (scheduledAction.pathwayShareAction) {
      const pathwaysCount =
        scheduledAction.pathwayShareAction.actionPathways.length
      headerObject.actionName +=
        pathwaysCount > 1
          ? t`${pathwaysCount} pathways`
          : t`${scheduledAction.pathwayShareAction.actionPathways[0].pathway.title} pathway`
    }

    if (scheduledAction.certificationShareAction) {
      const certificationsCount =
        scheduledAction.certificationShareAction.actionCertifications.length
      headerObject.actionName +=
        certificationsCount > 1
          ? t`${certificationsCount} certifications`
          : t`${scheduledAction.certificationShareAction.actionCertifications[0].certification.name} certification`
    }

    if (scheduledAction.surveyShareAction) {
      const surveysCount =
        scheduledAction.surveyShareAction.actionSurveys.length
      headerObject.actionName +=
        surveysCount > 1
          ? t`${surveysCount} surveys`
          : t`${scheduledAction.surveyShareAction.actionSurveys[0].survey.title} survey`
    }

    if (scheduledAction.email) {
      headerObject.actionName = t`${actionOrder}. Send email`
    }

    headerObjectList.push(
      {
        ...headerObject,
        actionName: t`${headerObject.actionName} (Scheduled)`,
        actionType: ReportTypeEnum.SCHEDULED
      },
      {
        ...headerObject,
        actionName: t`${headerObject.actionName} (Sent/Shared)`,
        actionType: ReportTypeEnum.ACTIVITY
      },
      {
        ...headerObject,
        actionName: t`${headerObject.actionName} (Started)`,
        actionType: ReportTypeEnum.ENGAGEMENT
      },
      {
        ...headerObject,
        actionName: t`${headerObject.actionName} (Completed)`,
        actionType: ReportTypeEnum.SUCCESS
      }
    )
  }

  if (scheduledAction.banner) {
    headerObject.actionName = t`${actionOrder}. Show banner ${scheduledAction.banner.header}`
    headerObjectList.push(
      {
        ...headerObject,
        actionType: ReportTypeEnum.SCHEDULED,
        actionName: t`${headerObject.actionName} (Scheduled)`
      },
      {
        ...headerObject,
        actionType: ReportTypeEnum.ENGAGEMENT,
        actionName: t`${headerObject.actionName} (First shown)`
      },
      {
        ...headerObject,
        actionType: ReportTypeEnum.SUCCESS,
        actionName: t`${headerObject.actionName} (First clicked)`
      }
    )
  }

  if (scheduledAction.message) {
    headerObject.actionName += t`Send message`

    headerObjectList.push(
      {
        ...headerObject,
        actionName: t`${headerObject.actionName} (Scheduled)`,
        actionType: ReportTypeEnum.SCHEDULED
      },
      {
        ...headerObject,
        actionName: t`${headerObject.actionName} (Completed)`,
        actionType: ReportTypeEnum.SUCCESS
      }
    )
  }

  return headerObjectList
}

const mapUserDataToCsvColumns = (
  userReportRow: CampaignUserReportRow,
  csvColumns: ICsvHeader[]
) => {
  const singleCsvRow: Record<string, any> = {
    [t`Username`]: userReportRow.user?.display_name,
    [t`User ID`]: userReportRow.user?.id,
    [t`First joined`]: userReportRow.firstJoinDate,
    [t`Status`]:
      userReportRow.lastCampaignEvent === CampaignUserEventTypeEnum.join
        ? t`Active`
        : t`Completed`
  }

  // Find value for every CSV header. If value is not found, empty string is passed.
  csvColumns.forEach((csvColumn) => {
    const { actionType } = csvColumn
    let value = ''
    let scheduledAction: ScheduledActionReport | undefined

    if (!Array.isArray(userReportRow.scheduledActions)) {
      return ''
    }

    if (csvColumn.scheduledAction.topicShareAction) {
      scheduledAction = userReportRow.scheduledActions.find(
        (scheduledAction) => {
          return (
            scheduledAction.campaign_topic_share_action_id ===
            (
              csvColumn.scheduledAction
                .topicShareAction as CampaignTopicShareActionModelAsEditor
            ).id
          )
        }
      )

      if (!scheduledAction) {
        if (actionType === ReportTypeEnum.SCHEDULED) {
          value = t`Not scheduled`
        }
      } else {
        value =
          createEnrolmentReport(
            actionType,
            scheduledAction,
            scheduledAction?.topicEnrolmentsEvent as EnrolmentEvent
          ) || ''
      }
    }

    if (csvColumn.scheduledAction.pathwayShareAction) {
      scheduledAction = userReportRow.scheduledActions.find(
        (scheduledAction) => {
          return (
            scheduledAction.campaign_pathway_share_action_id ===
            (
              csvColumn.scheduledAction
                .pathwayShareAction as CampaignPathwayShareActionModelAsEditor
            ).id
          )
        }
      )

      if (!scheduledAction) {
        if (actionType === ReportTypeEnum.SCHEDULED) {
          value = t`Not scheduled`
        }
      } else {
        value =
          createEnrolmentReport(
            actionType,
            scheduledAction,
            scheduledAction?.pathwayEnrolmentsEvent as EnrolmentEvent
          ) || ''
      }
    }

    if (csvColumn.scheduledAction.certificationShareAction) {
      scheduledAction = userReportRow.scheduledActions.find(
        (scheduledAction) => {
          return (
            scheduledAction.campaign_certification_share_action_id ===
            (
              csvColumn.scheduledAction
                .certificationShareAction as CampaignCertificationShareActionModel
            ).id
          )
        }
      )

      if (!scheduledAction) {
        if (actionType === ReportTypeEnum.SCHEDULED) {
          value = t`Not scheduled`
        }
      } else {
        value =
          createEnrolmentReport(
            actionType,
            scheduledAction,
            scheduledAction?.certificationEnrolmentsEvent as EnrolmentEvent
          ) || ''
      }
    }

    if (csvColumn.scheduledAction.surveyShareAction) {
      scheduledAction = userReportRow.scheduledActions.find(
        (scheduledAction) => {
          return (
            scheduledAction.campaign_survey_share_action_id ===
            (
              csvColumn.scheduledAction
                .surveyShareAction as CampaignSurveyShareActionModelAsEditor
            ).id
          )
        }
      )

      if (!scheduledAction) {
        if (actionType === ReportTypeEnum.SCHEDULED) {
          value = t`Not scheduled`
        }
      } else {
        value =
          createEnrolmentReport(
            actionType,
            scheduledAction,
            scheduledAction?.surveysEvent as EnrolmentEvent
          ) || ''
      }
    }

    if (csvColumn.scheduledAction.email) {
      scheduledAction = userReportRow.scheduledActions.find(
        (scheduledAction) => {
          return (
            scheduledAction.campaign_email_action_id ===
            (
              csvColumn.scheduledAction
                .email as CampaignEmailActionModelAsEditor
            ).id
          )
        }
      )

      if (!scheduledAction) {
        if (actionType === ReportTypeEnum.SCHEDULED) {
          value = t`Not scheduled`
        }
      } else {
        value = createEmailShareReport(actionType, scheduledAction)
      }
    }

    if (csvColumn.scheduledAction.banner) {
      scheduledAction = userReportRow.scheduledActions.find(
        (scheduledAction) => {
          return (
            scheduledAction.campaign_banner_id ===
            (csvColumn.scheduledAction.banner as CampaignBannerModelAsEditor).id
          )
        }
      )

      if (!scheduledAction) {
        if (actionType === ReportTypeEnum.SCHEDULED) {
          value = t`Not scheduled`
        }
      } else {
        value = createBannerReport(actionType, scheduledAction)
      }
    }

    if (csvColumn.scheduledAction.message) {
      scheduledAction = userReportRow.scheduledActions.find(
        (scheduledAction) => {
          return (
            scheduledAction.campaign_message_action_id ===
            (
              csvColumn.scheduledAction
                .message as CampaignMessageActionModelAsEditor
            ).id
          )
        }
      )

      if (!scheduledAction) {
        if (actionType === ReportTypeEnum.SCHEDULED) {
          value = t`Not scheduled`
        }
      } else {
        value = createMessageReport(actionType, scheduledAction)
      }
    }

    singleCsvRow[csvColumn.actionName] = value
  })
  return singleCsvRow
}

const createEnrolmentReport = (
  actionType: ReportTypeEnum,
  scheduledAction: ScheduledActionReport,
  enrolmentEvent: EnrolmentEvent
) => {
  const { processedNotification } = scheduledAction

  if (processedNotification && hasNotificationError(processedNotification)) {
    if (actionType === ReportTypeEnum.SCHEDULED) {
      return t`FAILED`
    }
    return ''
  }

  if (actionType === ReportTypeEnum.SCHEDULED) {
    return scheduledAction.scheduled_time
  }

  if (!enrolmentEvent) {
    return ''
  }

  if (actionType === ReportTypeEnum.ACTIVITY) {
    return enrolmentEvent.sent
  }
  if (actionType === ReportTypeEnum.ENGAGEMENT) {
    return enrolmentEvent.started
  }
  if (actionType === ReportTypeEnum.SUCCESS) {
    return enrolmentEvent.completed
  }
}

const createEmailShareReport = (
  actionType: ReportTypeEnum,
  scheduledAction
) => {
  const { processedNotification } = scheduledAction

  if (processedNotification && hasNotificationError(processedNotification)) {
    if (actionType === ReportTypeEnum.SCHEDULED) {
      return t`FAILED`
    }
    return ''
  }

  if (actionType === ReportTypeEnum.SCHEDULED) {
    return scheduledAction.scheduled_time
  }
  if (actionType === ReportTypeEnum.ACTIVITY) {
    return scheduledAction.emailEvent.sent
  }
  if (actionType === ReportTypeEnum.ENGAGEMENT) {
    return scheduledAction.emailEvent.opened
  }
  if (actionType === ReportTypeEnum.SUCCESS) {
    return scheduledAction.emailEvent.clicked
  }
}

const createMessageReport = (actionType: ReportTypeEnum, scheduledAction) => {
  const { processedNotification } = scheduledAction

  if (processedNotification && hasNotificationError(processedNotification)) {
    if (actionType === ReportTypeEnum.SCHEDULED) {
      return t`FAILED`
    }
    return ''
  }

  if (actionType === ReportTypeEnum.SCHEDULED) {
    return scheduledAction.scheduled_time
  }
  if (actionType === ReportTypeEnum.SUCCESS) {
    return scheduledAction.messageEvent.sent
  }
}

const createBannerReport = (actionType: ReportTypeEnum, scheduledAction) => {
  if (actionType === ReportTypeEnum.SCHEDULED) {
    return scheduledAction.scheduled_time
  }

  if (actionType === ReportTypeEnum.ENGAGEMENT) {
    return scheduledAction.bannerEvent.shown
  }
  if (actionType === ReportTypeEnum.SUCCESS) {
    return scheduledAction.bannerEvent.clicked
  }
}

export default CampaignActionReportDownloadCSV
